/**
 * Add playtest — INVENTORY-DESKTOP §4 row 3 (lane D4). Prototype lines 876-955.
 * Mounted by `DesktopApp` while `s.ptAddOpen`; closes via `closePtAdd()`.
 *
 * `selectors.3e#ptAddVM` + `ptPartyChips` unchanged. Every validation string
 * lives in the playtests slice / `lib/logic#ptCheckUrl` and arrives as a toast
 * or as `ptAddChk.err`; this file never re-implements one (INVENTORY §8.6). The
 * party-size gate is a real gate — `ptDoTrack` / `ptDoAddOff` refuse with
 * `PARTY SIZE REQUIRED` until one is picked.
 *
 * DESKTOP LAYOUT vs the mobile sheet
 *   CHECK      sits BESIDE the URL input on one row (mobile stacked it full
 *              width), so the idle label is the prototype's short `CHECK`
 *   party      a wrapping `padding:7px 13px` chip row, not six 44px cells
 *   off-Steam  the four fields are a `grid-template-columns:1fr 1fr` — name and
 *              URL share the top row, the two dates the bottom (mobile stacked
 *              name and URL and only paired the dates)
 *
 * `CHECKING…` is not the prototype's — it is the app's real busy state for the
 * round trip, kept here because the request is real (commit "Add CHECKING… busy
 * state to the add-playtest CHECK button").
 *
 * The `✓ REQUEST-ACCESS WIDGET FOUND` panel is conditional on a widget really
 * being on the page (P6 F-2); the prototype asserted it unconditionally, so a
 * store page without one now reads `— NO REQUEST-ACCESS WIDGET` and does not
 * paint itself green.
 */

import { PtCover } from '../../components/PtArt.tsx';
import { PixelButton } from '../../components/PixelButton.tsx';
import { useQd } from '../../store/index.ts';
import { ptAddVM } from '../../store/selectors.3e.ts';
import type { PtPartyChip } from '../../store/selectors.3e.ts';
import { PT_ADD_OFF_FOOT_DESKTOP } from '../../store/selectors.d4.ts';
import { Modal } from '../components/Modal.tsx';
import type { CSSProperties } from 'react';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

const FIELD: CSSProperties = {
  background: 'var(--gt-field)',
  border: '1px solid var(--gt-border)',
  color: 'var(--gt-text)',
  fontFamily: MONO,
  fontSize: 10,
  padding: '9px 11px',
  outline: 'none',
  minWidth: 0,
};

const PANEL: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: '16px 18px 18px',
};

const FOOT: CSSProperties = {
  fontFamily: MONO,
  fontSize: '8.5px',
  lineHeight: 1.6,
  color: 'var(--gt-muted-2)',
};

/** `SOLO 2 3 4 5 6+` — a wrapping chip row on desktop (prototype 917 / 944). */
function PartyRow({ party, onPick }: { party: PtPartyChip[]; onPick: (n: number) => void }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontFamily: MONO, fontSize: 10, flexWrap: 'wrap' }}>
      {party.map((p) => (
        <div
          key={p.key}
          onClick={() => onPick(p.n)}
          style={{
            border: '1px solid ' + p.bd,
            background: p.bg,
            color: p.clr,
            fontWeight: 600,
            padding: '7px 13px',
            cursor: 'pointer',
          }}
        >
          {p.l}
        </div>
      ))}
    </div>
  );
}

/** The gate's headline. `tail` is the only half that differs between tabs. */
function PartyGate({ tail }: { tail: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap' }}>
      <span style={{ fontFamily: PIXEL, fontSize: 7, color: 'var(--gt-text)' }}>PARTY SIZE</span>
      <span style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.06em', color: 'var(--acc2)' }}>REQUIRED</span>
      <span style={{ fontFamily: MONO, fontSize: '8.5px', color: 'var(--gt-muted-2)' }}>{tail}</span>
    </div>
  );
}

export default function PtAddModal(): JSX.Element {
  const s = useQd();
  const v = ptAddVM(s);

  const tab = (clr: string, bd: string): CSSProperties => ({
    color: clr,
    borderBottom: '2px solid ' + bd,
    paddingBottom: 9,
    cursor: 'pointer',
  });

  return (
    <Modal
      label="Add playtest modal"
      width={580}
      onClose={s.closePtAdd}
      header={{ title: '+ ADD A PLAYTEST', titleClr: 'var(--gt-text)' }}
    >
      <div style={{ display: 'flex', gap: 20, padding: '14px 18px 0', fontFamily: MONO, fontSize: 11, letterSpacing: '.05em' }}>
        <div onClick={() => s.setPtAddTab('steam')} style={tab(v.tabSClr, v.tabSBd)}>
          STEAM LINK
        </div>
        <div onClick={() => s.setPtAddTab('off')} style={tab(v.tabOClr, v.tabOBd)}>
          OFF-STEAM
        </div>
      </div>

      {v.isSteam ? (
        <div style={PANEL}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={v.url}
              onChange={(e) => s.setPtAddUrl(e.target.value)}
              placeholder="https://store.steampowered.com/app/…"
              style={{
                ...FIELD,
                flex: 1,
                border: '1px solid var(--gt-border-2)',
                color: 'var(--gt-text-2)',
                fontSize: '10.5px',
                padding: '10px 12px',
              }}
            />
            <div
              onClick={() => void s.ptDoCheck()}
              style={{
                border: '1px solid var(--gt-text)',
                color: 'var(--gt-text)',
                fontFamily: MONO,
                fontSize: '10.5px',
                padding: '10px 14px',
                cursor: v.checking ? 'progress' : 'pointer',
                whiteSpace: 'nowrap',
                flex: 'none',
              }}
            >
              {v.checking ? 'CHECKING…' : 'CHECK'}
            </div>
          </div>

          {v.hasErr ? (
            <div
              style={{
                border: '1px dashed var(--acc2)',
                padding: '12px 14px',
                fontFamily: MONO,
                fontSize: 10,
                lineHeight: 1.7,
                color: 'var(--acc2)',
              }}
            >
              ✕ {v.err}
            </div>
          ) : null}

          {v.hasOk ? (
            <div
              style={{
                border: '1px solid ' + v.okBd,
                background: v.okBg,
                padding: '13px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              <div style={{ fontFamily: PIXEL, fontSize: 7, color: v.okClr }}>{v.okTitle}</div>

              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ position: 'relative', width: 104, aspectRatio: '460/215', flex: 'none', background: v.fall, overflow: 'hidden' }}>
                  <PtCover style={{ position: 'absolute', inset: 0 }} fs={10} id={'chk' + v.appid} ini={v.ini} cands={v.cands} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--gt-text)' }}>{v.name}</div>
                  <div style={{ fontFamily: MONO, fontSize: '9.5px', color: 'var(--gt-text-5)' }}>{v.dev} · UNRELEASED</div>
                  <div style={{ fontFamily: MONO, fontSize: '9.5px', color: v.okClr }}>{v.sub}</div>
                </div>
              </div>

              <div
                style={{
                  borderTop: '1px dashed var(--gt-border)',
                  paddingTop: 11,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 9,
                }}
              >
                <PartyGate tail="— HOW MANY OF US CAN PLAY IN THE SAME GAME? STEAM DOESN'T SAY" />
                <PartyRow party={v.party} onPick={s.setPtAddParty} />
              </div>

              <PixelButton
                label={v.trackLbl}
                busy={v.tracking}
                onClick={() => void s.ptDoTrack()}
                fs={8}
                pad="12px 10px"
                center
                lh="1"
                shadow={false}
                style={{ width: '100%' }}
              />

              <div style={FOOT}>
                STATUS RE-CHECKED NIGHTLY · OPENS/CLOSES + DETECTED WAVES POST TO WHAT&apos;S NEW · ATTRIBUTED TO YOU
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div style={PANEL}>
          <div style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.08em', color: 'var(--gt-muted-2)' }}>
            FOR BUNGIE.NET / DEV-SITE / DISCORD-ANNOUNCED TESTS — USER-MAINTAINED, EVERY EDIT ATTRIBUTED
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <input
              value={v.offName}
              onChange={(e) => s.setPtOff({ ptOffName: e.target.value })}
              placeholder="GAME NAME"
              style={FIELD}
            />
            <input
              value={v.offUrl}
              onChange={(e) => s.setPtOff({ ptOffUrl: e.target.value })}
              placeholder="OFFICIAL SIGNUP URL"
              style={FIELD}
            />
            <input
              type="date"
              value={v.offStart}
              onChange={(e) => s.setPtOff({ ptOffStart: e.target.value })}
              title="Window start — optional"
              style={{ ...FIELD, color: 'var(--gt-text-5)' }}
            />
            <input
              type="date"
              value={v.offEnd}
              onChange={(e) => s.setPtOff({ ptOffEnd: e.target.value })}
              title="Window end — optional"
              style={{ ...FIELD, color: 'var(--gt-text-5)' }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            <PartyGate tail="— HOW MANY OF US CAN PLAY IN THE SAME GAME?" />
            <PartyRow party={v.party} onPick={s.setPtAddParty} />
          </div>

          <PixelButton
            label={v.trackLbl}
            busy={v.tracking}
            onClick={() => void s.ptDoAddOff()}
            fs={8}
            pad="12px 10px"
            center
            lh="1"
            shadow={false}
            style={{ width: '100%' }}
          />

          <div style={FOOT}>{PT_ADD_OFF_FOOT_DESKTOP}</div>
        </div>
      )}
    </Modal>
  );
}
